import api from '../services/api';
import { unwrapDek, wrapDek, importPublicKey } from './fieldEncryption';

/**
 * Handing a recipient the key to ONE node.
 *
 * A grant makes a node visible; it does not make it readable. For a node whose
 * schema declares encrypted fields, the content is sealed under a per-node DEK
 * (crypto v3) and the recipient can list the node and see ciphertext until they
 * hold a copy of that DEK wrapped to their own public key.
 *
 * Which is a failure mode with no error attached: the share succeeds, the node
 * appears, and the person opens gibberish. So the wrap runs as part of sharing,
 * and when it cannot run the caller is told exactly why.
 *
 * The server never sees a raw DEK or a private key. It stores opaque blobs; the
 * unwrap-and-rewrap happens here, in the browser, with the owner's in-memory key.
 *
 *   owner's wrapped DEK  --unwrap(owner private key)-->  DEK
 *   DEK  --wrap(recipient public key)-->  their wrapped DEK  --> POST share-key
 *
 * v3 is what makes this scoped. Under v2 the same act meant delegating the
 * owner's whole private key — every encrypted node, not this one — which is
 * why a v2 node is refused here rather than quietly over-shared.
 */

export const KEY_OUTCOME = {
  NO_REALM_KEY: 'no_realm_key',     // realm has no keypair yet
  NOT_ENCRYPTED: 'not_encrypted',   // nothing to wrap; the share is complete
  DONE: 'done',
  LOCKED: 'locked',                 // owner's key not unlocked this session
  NO_RECIPIENT_KEY: 'no_recipient_key',
  NO_OWNER_KEY: 'no_owner_key',     // v2 node, or never sealed
  FAILED: 'failed',
};

/** Does this node hold encrypted content at all? */
export function isEncryptedNode(node, schema) {
  const declared = schema?.encrypted_fields || node?.encrypted_fields || [];
  if (Array.isArray(declared) && declared.length > 0) return true;
  // Fall back to the wire format — a node can carry ciphertext whose schema was
  // edited afterwards, and it is still unreadable without the key.
  return JSON.stringify(node?.value_json || {}).includes('paradigm_enc:');
}

/** The node's DEK, unwrapped in memory. Returns {dek} or {problem}. */
async function ownerDek(node, encKeys) {
  if (!encKeys?.privateKey) {
    return { problem: {
      outcome: KEY_OUTCOME.LOCKED,
      message: 'This node is encrypted. Unlock your encryption key to let them read it — '
        + 'the grant is in place, but the content stays unreadable until you do.',
    } };
  }
  let wrapped;
  try {
    wrapped = (await api.request(`/nodes/${node.id}/key`))?.wrapped_dek;
  } catch {
    return { problem: {
      outcome: KEY_OUTCOME.NO_OWNER_KEY,
      message: 'This node is encrypted in the older format, which has no per-node key. '
        + 'Sharing it readably would mean delegating the key to all of your encrypted data, '
        + 'so it is not offered here — it can be seen listed, not read.',
    } };
  }
  try {
    return { dek: await unwrapDek(wrapped, encKeys.privateKey) };
  } catch {
    return { problem: { outcome: KEY_OUTCOME.FAILED, message: 'Your key could not open this node.' } };
  }
}

/** base64url → CryptoKey. */
async function publicKeyFrom(b64) {
  const raw = Uint8Array.from(
    atob(String(b64).replace(/-/g, '+').replace(/_/g, '/')),
    c => c.charCodeAt(0),
  );
  return importPublicKey(raw);
}

/**
 * Give a REALM the DEK for `node`, so every current and future grantee of that
 * realm can read it.
 *
 * A realm's copy is wrapped to the realm's own keypair rather than to any
 * person — which is what lets somebody added to the realm next month read a
 * node shared into it today, without the owner being present to re-wrap.
 */
export async function grantRealmNodeKey({ node, schema, realmId, encKeys }) {
  if (!isEncryptedNode(node, schema)) return { outcome: KEY_OUTCOME.NOT_ENCRYPTED };

  const { dek, problem } = await ownerDek(node, encKeys);
  if (problem) return problem;

  let realmPub;
  try {
    const res = await api.request(`/realms/${realmId}/enc-public-key`);
    realmPub = await publicKeyFrom(res.enc_public_key);
  } catch {
    // The realm has no keypair yet. Minting one is a realm-level act with its
    // own rotation semantics, so this stops rather than quietly creating key
    // material on the owner's behalf from a node row.
    return {
      outcome: KEY_OUTCOME.NO_REALM_KEY,
      message: 'This realm has no encryption key yet, so the node was added but stays '
        + 'unreadable to its members. Set the realm up for encryption in Privacy realms.',
    };
  }

  try {
    await api.request(`/nodes/${node.id}/realm-key`, {
      method: 'POST',
      body: { realm_id: realmId, wrapped_dek: await wrapDek(dek, realmPub) },
    });
    return { outcome: KEY_OUTCOME.DONE };
  } catch {
    return {
      outcome: KEY_OUTCOME.FAILED,
      message: 'The node was added to the realm but the key could not be attached, so it '
        + 'stays unreadable there. Trying again is safe.',
    };
  }
}

/**
 * Give `recipientUserId` the DEK for `node`, scoped to the share `epId`.
 * Returns { outcome, message? } and never throws — the share already happened,
 * so this reports rather than unwinds.
 */
export async function grantNodeKey({ node, schema, recipientUserId, epId, encKeys }) {
  if (!isEncryptedNode(node, schema)) {
    return { outcome: KEY_OUTCOME.NOT_ENCRYPTED };
  }

  const { dek, problem } = await ownerDek(node, encKeys);
  if (problem) return problem;

  let recipientPub;
  try {
    const res = await api.request(`/users/${recipientUserId}/enc-public-key`);
    if (!res?.enc_public_key) throw new Error('no key');
    recipientPub = await publicKeyFrom(res.enc_public_key);
  } catch {
    return {
      outcome: KEY_OUTCOME.NO_RECIPIENT_KEY,
      message: 'They have not set up encryption yet, so there is no key to wrap this for. '
        + 'They will see the node listed but not its contents — share it again once they have.',
    };
  }

  try {
    await api.request(`/nodes/${node.id}/share-key`, {
      method: 'POST',
      body: {
        recipient_user_id: recipientUserId,
        wrapped_dek: await wrapDek(dek, recipientPub),
        ep_id: epId,
      },
    });
    return { outcome: KEY_OUTCOME.DONE };
  } catch (err) {
    return {
      outcome: KEY_OUTCOME.FAILED,
      message: 'The grant is in place but the key could not be handed over, so the content '
        + 'stays unreadable to them. Trying again is safe.',
    };
  }
}
